import { useTranslation } from 'react-i18next';

import { Header } from '../../../../../../components/atoms/header/header';
import { useAllIndicators } from '../../../../context/indicators';
import {
  useAllRespondentTypes,
  useAnswersForRespondentType,
  useRenderContext,
  useSurveyForRespondentType,
} from '../../../../context/render-context';
import { AnsweredStatus, useConditions } from '../../../../utils/surveys/conditions';
import * as expressionUtils from '../../../../utils/surveys/expressions';
import { useExpression } from '../hooks/use-expression';
import { DetailsScoringProps, MeasureDetailsProps, MeasureScoreSummaryProps } from '../types';

import { ClinicalReference } from './clinical-reference';
import { DetailsScoring } from './details-scoring';
import { ErrorBlock } from './error-block';
import { MeasureScoreSummary } from './measure-score-summary';

type DetailScoringForRespondentProps = Omit<DetailsScoringProps, 'expression' | 'answeredStatus' | 'value'> & {
  readonly expressionName: string;
};
const DetailScoringForRespondent = ({ expressionName, ...restProps }: DetailScoringForRespondentProps) => {
  const survey = useSurveyForRespondentType(restProps.respondentType);
  const answers = useAnswersForRespondentType(restProps.respondentType);
  const expression = useExpression({ survey, selectedExpressionName: expressionName });
  const { answeredStatus } = useConditions(expression, answers);

  if (!expression) return <ErrorBlock error="no-expression"></ErrorBlock>;
  if (!expressionUtils.isMeasureExpression(expression)) return <ErrorBlock error="wrong-expression"></ErrorBlock>;

  if (!survey || !answers || answeredStatus === AnsweredStatus.NOT_SHOWN) return null;

  const measureValue = expressionUtils.getMeasureValue(answers[expression.name]);

  return <DetailsScoring answeredStatus={answeredStatus} expression={expression} value={measureValue} {...restProps} />;
};

type MeasureScoreSummaryForRespondentProps = Omit<
  MeasureScoreSummaryProps,
  'answeredStatus' | 'answers' | 'survey' | 'expression' | 'value' | 'thresholdByScore'
> & {
  readonly expressionName: string;
};
const MeasureScoreSummaryForRespondent = ({ expressionName, ...restProps }: MeasureScoreSummaryForRespondentProps) => {
  const survey = useSurveyForRespondentType(restProps.respondentType);
  const answers = useAnswersForRespondentType(restProps.respondentType);
  const expression = useExpression({ survey, selectedExpressionName: expressionName });
  const { answeredStatus } = useConditions(expression, answers);

  if (!expression) return null;
  if (!survey || !answers) return null;
  if (!expressionUtils.isMeasureExpression(expression)) return <ErrorBlock error="wrong-expression"></ErrorBlock>;

  const measureValue = expressionUtils.getMeasureValue(answers[expression.name]);
  const thresholdByScore = expressionUtils.getThresholdByScore(measureValue, expression);

  return (
    <MeasureScoreSummary
      answeredStatus={answeredStatus}
      answers={answers}
      expression={expression}
      survey={survey}
      thresholdByScore={thresholdByScore}
      value={measureValue}
      {...restProps}
    />
  );
};

export const MeasureDetails = ({
  title,
  description,
  expression,
  allRespondents,
  allSkipped,
  hideMainMeasure,
  isPdf = false,
}: MeasureDetailsProps) => {
  const { t } = useTranslation();
  const allRespondentTypes = useAllRespondentTypes();
  const allIndicators = useAllIndicators();
  const { selectedRespondentType } = useRenderContext();
  if (!selectedRespondentType) return <div>No respondent type selected</div>;

  const respondentTypes = allRespondents ? allRespondentTypes : [selectedRespondentType];
  const showIndicators = allIndicators.length > 1;

  return (
    <div className="flex gap-1 flex-col" id={expression.name}>
      <Header level="h3">{title}</Header>
      {respondentTypes.map((rt) => (
        <MeasureScoreSummaryForRespondent
          expressionName={expression.name}
          hideMainMeasure={hideMainMeasure}
          isPdf={isPdf}
          key={rt}
          respondentType={rt}
          showIndicators={showIndicators}
        />
      ))}
      {!allSkipped && (
        <ClinicalReference
          description={description}
          linkText={t('report.blocks.measureBreakdown.clinicalReferenceLink')}
        />
      )}
      {!hideMainMeasure &&
        respondentTypes.map((rt) => (
          <DetailScoringForRespondent
            description={description}
            expressionName={expression.name}
            key={rt}
            respondentType={rt}
            showIndicators={showIndicators}
          />
        ))}
    </div>
  );
};
